<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue';
import { useTranslation } from 'i18next-vue';
import ProgressSpinner from 'primevue/progressspinner';
import { useHubspotForm } from '@peanuds/app-shell';
import { UiDialogCloseButton, UiDialogShell } from '@peanuds/ui';

interface Props {
  visible: boolean;
}

interface Emits {
  'update:visible': [value: boolean];
}

const FORM_LOAD_TIMEOUT_MS = 10000;
const FORM_POLL_INTERVAL_MS = 150;
const FORM_RENDERED_MIN_HEIGHT = 40;

const { visible } = defineProps<Props>();

const emit = defineEmits<Emits>();

const { t } = useTranslation();
const { isFormAvailable, loadForm, portalId, formId, region } = useHubspotForm();

const isFormLoading = ref(false);

let pollTimer: ReturnType<typeof setInterval> | undefined;

const stopPolling = () => {
  clearInterval(pollTimer);
  pollTimer = undefined;
};

// Check if the form is rendered by looking for the iframe or form element and ensuring it has a minimum height
const isFormRendered = () => {
  const content = document.querySelector<HTMLElement>('.hs-form-frame iframe, .hs-form-frame form');
  return !!content && content.offsetHeight > FORM_RENDERED_MIN_HEIGHT;
};

const close = () => emit('update:visible', false);

watch(
  () => visible,
  (isVisible) => {
    stopPolling();

    if (!isVisible || !isFormAvailable.value) {
      return;
    }

    isFormLoading.value = true;
    loadForm();

    const startedAt = Date.now();
    pollTimer = setInterval(() => {
      if (!isFormRendered() && Date.now() - startedAt < FORM_LOAD_TIMEOUT_MS) {
        return;
      }
      isFormLoading.value = false;
      stopPolling();
    }, FORM_POLL_INTERVAL_MS);
  },
  { immediate: true },
);

onBeforeUnmount(stopPolling);
</script>

<template>
  <UiDialogShell
    :visible="visible"
    :dismissable-mask="false"
    surface="white"
    max-width="90vw"
    :aria-label="t('totalBalance.pre_order.aria_label')"
    @update:visible="$emit('update:visible', $event)"
  >
    <template #surface>
      <div class="pre-order">
        <UiDialogCloseButton label="Close" @click="close" />

        <div class="pre-order__content">
          <div v-if="isFormAvailable" class="pre-order__form">
            <div v-show="isFormLoading" class="pre-order__form-loader">
              <ProgressSpinner
                class="pre-order__form-spinner"
                strokeWidth="6"
                animationDuration="1s"
                aria-label="Loading pre-order form"
              />
            </div>
            <div
              class="hs-form-frame"
              :data-region="region"
              :data-form-id="formId"
              :data-portal-id="portalId"
            />
          </div>
          <div v-else class="pre-order__unavailable" role="status">
            <h2>{{ t('totalBalance.pre_order.unavailable_title') }}</h2>
            <p>{{ t('totalBalance.pre_order.unavailable_description') }}</p>
          </div>
        </div>
      </div>
    </template>
  </UiDialogShell>
</template>

<style lang="scss" scoped>
.pre-order {
  display: flex;
  flex-direction: column;
  position: relative;

  &__content {
    display: flex;
    flex-direction: column;
    gap: 24px;
    position: relative;
    z-index: 1;
  }

  &__form {
    position: relative;
    width: 100%;
    min-height: 24rem;
  }

  &__form-loader {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__form-spinner {
    width: 2.5rem;
    height: 2.5rem;
  }

  &__unavailable {
    display: flex;
    padding: ($static-space-10 * 2) $static-space-10 $static-space-10;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: $static-space-4;
    text-align: center;

    h2,
    p {
      margin: 0;
    }

    h2 {
      color: inherit;
      font-size: $static-text-xl;
      font-weight: $font-weight-semibold;
    }

    p {
      color: $color-text-muted;
      font-size: $static-text-sm;
    }
  }
}
</style>
